import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, AlertTriangle, XCircle, X } from 'lucide-react'
import { useSSE } from '@/hooks/useSSE'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { getDevices } from '@/api/devices'
import { getUsers, type User } from '@/api/users'
import { findActiveMembership } from '@/api/memberships'
import { membershipMessage, type MembershipTone } from '@/lib/membership-status'

interface CheckIn {
  key: number
  userId: string | null
  name: string
  message: string
  tone: MembershipTone
}

const AUTO_DISMISS_MS = 8000

const TONE_STYLES: Record<CheckIn['tone'], { text: string; ring: string; bg: string; border: string; bar: string; icon: React.ReactNode }> = {
  ok: {
    text: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-500/30', bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/25', bar: 'bg-emerald-500', icon: <CheckCircle2 className="w-full h-full" />,
  },
  warn: {
    text: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-500/30', bg: 'bg-amber-500/10',
    border: 'border-amber-500/25', bar: 'bg-amber-500', icon: <AlertTriangle className="w-full h-full" />,
  },
  expired: {
    text: 'text-red-600 dark:text-red-400', ring: 'ring-red-500/30', bg: 'bg-red-500/10',
    border: 'border-red-500/25', bar: 'bg-red-500', icon: <XCircle className="w-full h-full" />,
  },
}

/**
 * Non-blocking notice shown the moment a member badges in/out at any device,
 * so the front desk can act on it right away (renew a lapsed membership, let
 * someone in manually, etc.) instead of finding out after the fact. Driven by
 * the SSE stream — see useSSE's watchdog for why this no longer needs a page
 * refresh to keep working. Auto-dismisses on its own (pauses while hovered so
 * you don't lose it mid-read); a new check-in replaces whatever's currently
 * showing instead of stacking.
 */
export function CheckInToast() {
  const navigate = useNavigate()
  const { proxyUrl, proxyToken, isElectron } = useProxyConfig()
  const [deviceIds, setDeviceIds] = useState<string[]>([])
  const [checkIn, setCheckIn] = useState<CheckIn | null>(null)
  const [leaving, setLeaving] = useState(false)
  const [barActive, setBarActive] = useState(false)
  const usersRef = useRef<User[]>([])
  const processedCount = useRef(0)
  const keyRef = useRef(0)
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { events } = useSSE(isElectron ? proxyUrl : null, isElectron ? proxyToken : null, deviceIds)

  useEffect(() => {
    getUsers().then(r => { usersRef.current = r.users }).catch(() => { /* best-effort */ })
    if (!isElectron) return
    getDevices().then(({ devices }) => setDeviceIds(devices.map(d => d.id))).catch(() => { /* best-effort */ })
  }, [isElectron])

  useEffect(() => {
    if (events.length <= processedCount.current) return
    // events[] is newest-first — only the freshest one we haven't seen yet matters.
    const newest = events[0]
    processedCount.current = events.length

    if (newest.type !== 'accessEvent' || !newest.employeeNo) return

    void (async () => {
      const user = usersRef.current.find(u => u.employee_no === newest.employeeNo)
      const name = user?.name ?? newest.name ?? newest.employeeNo!

      let message = 'Acceso denegado'
      let tone: CheckIn['tone'] = 'expired'
      if (newest.eventType === 'ACCESS_GRANTED') {
        message = 'Registrado'
        tone = 'ok'
        if (user) {
          try {
            const { membership } = await findActiveMembership(user.id)
            const m = membershipMessage(membership?.end_date)
            message = m.message
            tone = m.tone
          } catch { /* keep default */ }
        }
      }

      keyRef.current += 1
      setLeaving(false)
      setCheckIn({ key: keyRef.current, userId: user?.id ?? null, name, message, tone })
    })()
  }, [events])

  function dismiss() {
    if (dismissTimer.current) clearTimeout(dismissTimer.current)
    setLeaving(true)
    setTimeout(() => setCheckIn(null), 200) // matches exit animation duration
  }

  function startTimer() {
    if (dismissTimer.current) clearTimeout(dismissTimer.current)
    dismissTimer.current = setTimeout(dismiss, AUTO_DISMISS_MS)
  }

  // Kick off the auto-dismiss clock (and its progress bar) whenever a fresh
  // check-in appears. Two rAFs so the bar starts at full width before the
  // CSS transition to 0 is applied — otherwise the browser collapses the
  // "100% -> 0%" change into a single frame and it never animates.
  useEffect(() => {
    if (!checkIn) return
    startTimer()
    setBarActive(false)
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setBarActive(true))
    })
    return () => {
      cancelAnimationFrame(raf1)
      if (dismissTimer.current) clearTimeout(dismissTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn?.key])

  function handleManage() {
    if (!checkIn?.userId) return
    const id = checkIn.userId
    if (dismissTimer.current) clearTimeout(dismissTimer.current)
    setCheckIn(null)
    navigate(`/members/${id}`)
  }

  if (!checkIn) return null
  const s = TONE_STYLES[checkIn.tone]

  return (
    <div
      key={checkIn.key}
      onMouseEnter={() => { if (dismissTimer.current) clearTimeout(dismissTimer.current) }}
      onMouseLeave={startTimer}
      className={`fixed bottom-5 right-5 z-50 w-80 overflow-hidden rounded-2xl border ${s.border} ${s.bg} backdrop-blur-md shadow-xl transition-all duration-200 ease-out ${
        leaving ? 'opacity-0 translate-y-2 scale-95' : 'opacity-100 translate-y-0 scale-100 animate-in fade-in slide-in-from-bottom-4'
      }`}
      role="status"
    >
      <button
        onClick={dismiss}
        aria-label="Cerrar"
        className="absolute top-2.5 right-2.5 text-text-muted/60 hover:text-text-primary transition-colors rounded-full p-0.5 hover:bg-background/40"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-3 px-4 pt-4 pb-3">
        <div className={`shrink-0 w-9 h-9 rounded-full ring-2 ${s.ring} ${s.text} p-1.5 bg-background/40`}>
          {s.icon}
        </div>
        <div className="min-w-0 pr-4">
          <p className="text-text-primary font-bold text-sm truncate">{checkIn.name}</p>
          <p className={`text-xs font-medium ${s.text}`}>{checkIn.message}</p>
        </div>
      </div>

      {checkIn.userId && (
        <div className="px-4 pb-3">
          <button
            onClick={handleManage}
            className="w-full text-center py-1.5 bg-background/50 hover:bg-background/80 text-text-primary text-xs font-semibold rounded-lg transition-colors"
          >
            Gestionar usuario
          </button>
        </div>
      )}

      <div className="h-0.5 w-full bg-background/30">
        <div
          className={`h-full ${s.bar} transition-[width] ease-linear`}
          style={{ width: barActive ? '0%' : '100%', transitionDuration: `${AUTO_DISMISS_MS}ms` }}
        />
      </div>
    </div>
  )
}
